import { useEffect, useState, type ReactNode } from 'react';
import {
  AppBar, Avatar, Box, Chip, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography, alpha,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { BrandLogo } from '../components/BrandLogo';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';

export interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
}

const DRAWER_WIDTH = 240;

interface AppShellProps {
  nav: NavItem[];
  badge?: string;
  status?: ReactNode;
}

/** Shared exchange-style shell: glass topbar, responsive icon sidebar, user menu.
 *  Sidebar is a permanent drawer on md+ and a hamburger-toggled temporary drawer below. */
export function AppShell({ nav, badge, status }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const initials = (user?.fullName ?? 'U')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isActive = (to: string) =>
    location.pathname === to || (to !== '/admin' && location.pathname.startsWith(to));

  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ px: 1.5, pt: 2, pb: 1 }}>
        <Typography
          variant="caption"
          sx={{ px: 2, color: 'text.secondary', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}
        >
          Menu
        </Typography>
      </Box>
      <List sx={{ px: 0.5 }}>
        {nav.map((n) => {
          const active = isActive(n.to);
          return (
            <ListItemButton key={n.to} selected={active} onClick={() => navigate(n.to)}>
              <ListItemIcon sx={{ minWidth: 38, color: active ? 'primary.light' : 'text.secondary' }}>
                {n.icon}
              </ListItemIcon>
              <ListItemText
                primary={n.label}
                primaryTypographyProps={{ fontWeight: active ? 700 : 500, color: active ? '#fff' : 'text.secondary' }}
              />
              {active && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: '0 0 10px #4f8cff' }} />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between', gap: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen((o) => !o)}
              sx={{ display: { md: 'none' }, mr: 0.5 }}
              aria-label="Toggle navigation"
            >
              <MenuIcon />
            </IconButton>
            <BrandLogo height={46} />
            {badge && (
              <Chip
                size="small"
                label={badge}
                sx={{
                  ml: 0.5,
                  display: { xs: 'none', sm: 'flex' },
                  color: 'primary.light',
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.14),
                  border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
            {status}
            <Tooltip title="Account">
              <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #4f8cff, #16c784)',
                  }}
                >
                  {initials}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{user?.fullName}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user?.role}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { setAnchor(null); setPwOpen(true); }} sx={{ gap: 1.5, mt: 0.5 }}>
                <LockResetIcon fontSize="small" /> Change Password
              </MenuItem>
              <MenuItem onClick={logout} sx={{ color: 'error.main', gap: 1.5 }}>
                <LogoutIcon fontSize="small" /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile: temporary overlay drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop: permanent drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          mt: 8,
          minWidth: 0,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Outlet />
      </Box>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </Box>
  );
}
